import { useCallback, useEffect, useState } from "react";
import type { Paginator } from "types";
import { useSimpleRequest } from "hooks/useRequest";
import { Paginate } from "consts/table";
import { SortOrder } from "consts/request-query";
import { RequestQueryMapper } from "util/request-query-mapper";
import { ManagementApiResponseFactory } from "util/api";
import { compareIds } from "util/support";
import { getPaymentMethods as getPaymentMethodsRequest } from "features/paymentMethods/api";
import type { PaymentMethod } from "features/paymentMethods/types";
import { stripForDuplicate } from "../PaymentMethod/PaymentMethodProvider";

const DEFAULT_PAGE_SIZE = 10;

export default function usePaymentMethods() {
    const request = useSimpleRequest();
    const [isPaymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
    const [paymentMethodsPage, setPaymentMethodsPage] = useState<Paginator<PaymentMethod>>(
        () => ManagementApiResponseFactory.makePaginator<PaymentMethod>()
    );
    const { data: _, ...paginator } = paymentMethodsPage;

    const [duplicateSeed, setDuplicateSeed] = useState<PaymentMethod | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [searchCriteria, setSearchCriteriaState] = useState('');
    const [providers, setProvidersState] = useState<string[]>([]);
    const [tags, setTagsState] = useState<string[]>([]);
    const [providerOptions, setProviderOptions] = useState<string[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);

    useEffect(() => {
        let isActive = true;
        const distinctQuery = (field: 'PSP' | 'tag') => RequestQueryMapper.from()
            .distincts(field)
            .contains(Paginate.perPage, '1000')
            .toString();
        const getResources = (response: any): PaymentMethod[] => response?.data?.data ?? [];

        Promise.all([
            request(() => getPaymentMethodsRequest(distinctQuery('PSP'))),
            request(() => getPaymentMethodsRequest(distinctQuery('tag')))
        ]).then(([providerResponse, tagResponse]) => {
            if (!isActive) return;

            setProviderOptions(Array.from(new Set(
                getResources(providerResponse).map(paymentMethod => paymentMethod.PSP).filter(Boolean)
            )).sort());
            setTagOptions(Array.from(new Set(
                getResources(tagResponse).map(paymentMethod => paymentMethod.tag).filter(Boolean)
            )).sort());
        });

        return () => { isActive = false; };
    }, [request]);

    // The page keeps its own paginated result. The shared payment-methods slice holds the
    // full collection loaded at dashboard bootstrap; sharing it made that fetch-all
    // overwrite this page (and this page truncate it for every other screen).
    useEffect(() => {
        let isActive = true;
        const query = RequestQueryMapper.from()
            .contains(Paginate.page, String(page))
            .contains(Paginate.perPage, String(perPage))
            .sortBy('name')
            .sortOrder(SortOrder.Asc);

        if (searchCriteria.trim()) {
            query
                .checkWithModifier('name', 'contains')
                .values('name', searchCriteria.trim());
        }
        providers.forEach(provider => query.containsIn('PSP', provider));
        tags.forEach(tag => query.containsIn('tag', tag));

        setPaymentMethodsLoading(true);
        request(() => getPaymentMethodsRequest(query.toString()), {
            onSuccess: response => {
                if (!isActive) return;
                setPaymentMethodsPage(ManagementApiResponseFactory.makePaginator(response.data));
            }
        })
            .catch(() => {})
            .finally(() => {
                if (isActive) setPaymentMethodsLoading(false);
            });

        return () => { isActive = false; };
    }, [page, perPage, providers, request, searchCriteria, tags]);

    const upsertPaymentMethod = useCallback((paymentMethod: PaymentMethod) => {
        setPaymentMethodsPage(currentPage => {
            const isExisting = currentPage.data.some(({ coreId }) => compareIds(coreId, paymentMethod.coreId));

            return {
                ...currentPage,
                total: isExisting ? currentPage.total : currentPage.total + 1,
                data: isExisting
                    ? currentPage.data.map(current => compareIds(current.coreId, paymentMethod.coreId)
                        ? { ...current, ...paymentMethod }
                        : current)
                    : [paymentMethod, ...currentPage.data]
            };
        });
    }, []);

    // Bulk selection is scoped to the rows on screen; reset it whenever they change
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, perPage, providers, searchCriteria, tags]);

    const toggleSelection = useCallback((coreId: PaymentMethod['coreId'], checked: boolean) => {
        setSelectedIds(current => {
            const next = new Set(current);

            if (checked) {
                next.add(String(coreId));
            } else {
                next.delete(String(coreId));
            }

            return next;
        });
    }, []);

    const selectPage = (checked: boolean) => setSelectedIds(
        new Set(checked ? paymentMethodsPage.data.map(({ coreId }) => String(coreId)) : [])
    );

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const getPaymentMethods = () => paymentMethodsPage.data;

    const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        setPage(1);
    };

    const setSearchCriteria = resetPage(setSearchCriteriaState);
    const setProviders = resetPage(setProvidersState);
    const setTags = resetPage(setTagsState);

    const changePageSize = (value: number) => {
        setPerPage(value);
        setPage(1);
    };

    const requestDuplicate = useCallback((paymentMethod: PaymentMethod) => {
        setDuplicateSeed(stripForDuplicate(paymentMethod));
    }, []);

    const dismissDuplicate = useCallback(() => {
        setDuplicateSeed(null);
    }, []);

    return {
        getPaymentMethods,
        isPaymentMethodsLoading,
        paginator,
        page,
        perPage,
        providers,
        tags,
        searchCriteria,
        providerOptions,
        tagOptions,
        setPage,
        setPerPage: changePageSize,
        setProviders,
        setTags,
        setSearchCriteria,
        duplicateSeed,
        requestDuplicate,
        dismissDuplicate,
        upsertPaymentMethod,
        selectedIds,
        toggleSelection,
        selectPage,
        clearSelection
    };
};
